import { useEffect, useState } from 'react';
import FormControl from '@material-ui/core/FormControl';
import FormHelperText from '@material-ui/core/FormHelperText';
import InputLabel from '@material-ui/core/InputLabel';
import MenuItem from '@material-ui/core/MenuItem';
import Select from '@material-ui/core/Select';
import Typography from '@material-ui/core/Typography';
import Chip from '@material-ui/core/Chip';
import Box from '@material-ui/core/Box';
import { useApi } from '@backstage/core-plugin-api';
import { catalogApiRef } from '@backstage/plugin-catalog-react';

/**
 * Knative Resource 选项
 */
interface KnativeResourceOption {
  name: string;
  title: string;
  description: string;
  clusterName: string;
  namespace: string;
  environmentType: string;
  owner: string;
}

/**
 * KnativeResourcePicker
 *
 * 自定义字段：从 Backstage Catalog 选择 Knative Resource（type: knative-environment）
 *
 * 使用方式：
 * ```yaml
 * knativeResource:
 *   title: Knative 环境
 *   type: string
 *   ui:field: KnativeResourcePicker
 *   ui:options:
 *     environmentType: test  # 可选，按环境类型过滤
 * ```
 */
interface KnativeResourcePickerProps {
  onChange: (value: string | undefined) => void;
  rawErrors?: string[];
  required?: boolean;
  formData?: string;
  uiSchema?: Record<string, any>;
  idSchema?: { $id: string };
  schema?: { title?: string; description?: string };
}

export const KnativeResourcePicker = ({
  onChange,
  rawErrors,
  required,
  formData,
  uiSchema,
  idSchema,
  schema,
}: KnativeResourcePickerProps) => {
  const catalogApi = useApi(catalogApiRef);

  const [resources, setResources] = useState<KnativeResourceOption[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string>();

  // 从 uiSchema 获取过滤选项
  const environmentTypeFilter = uiSchema?.['ui:options']?.environmentType;

  useEffect(() => {
    const loadResources = async () => {
      try {
        setLoading(true);
        setError(undefined);

        // 从 Catalog 获取所有 Knative Resource 实体
        // 支持新的 knative-project 和旧的 knative-environment（向后兼容）
        const response = await catalogApi.getEntities({
          filter: {
            kind: 'Resource',
            'spec.type': ['knative-project', 'knative-environment'],
          },
        });

        let resourceList: KnativeResourceOption[] = response.items.map(entity => {
          const annotations = entity.metadata.annotations || {};
          
          // 优先从新的 knative.dev/* 注解获取实例信息
          const instanceName = annotations['knative.dev/instance-name'] || annotations['hose.io/knative-cluster'] || '';
          const instanceUrl = annotations['knative.dev/instance-url'] || annotations['hose.io/knative-cluster-url'] || '';
          const projectName = annotations['knative.dev/project-name'] || '';
          
          // 从 destinations 数组获取第一个 destination 的信息（向后兼容）
          let namespace = annotations['hose.io/knative-namespace'] || '';
          let clusterName = instanceName;
          
          // 尝试从新的 destinations 格式解析
          const destinationsStr = annotations['hose.io/knative-destinations'];
          if (destinationsStr) {
            try {
              const destinations = JSON.parse(destinationsStr);
              if (Array.isArray(destinations) && destinations.length > 0) {
                const firstDest = destinations[0];
                namespace = firstDest.namespace || namespace;
                clusterName = firstDest.clusterName || clusterName;
              }
            } catch {
              // 解析失败，使用旧格式
            }
          }
          
          return {
            name: entity.metadata.name,
            title: entity.metadata.title || entity.metadata.name,
            description: entity.metadata.description || '',
            clusterName,
            namespace,
            environmentType: annotations['hose.io/environment-type'] || '',
            owner: entity.spec?.owner as string || '',
          };
        });

        // 按环境类型过滤
        if (environmentTypeFilter) {
          resourceList = resourceList.filter(
            r => r.environmentType === environmentTypeFilter,
          );
        }

        // 按名称排序
        resourceList.sort((a, b) => a.name.localeCompare(b.name));

        setResources(resourceList);
      } catch (err) {
        setError(
          err instanceof Error ? err.message : 'Failed to load Knative resources',
        );
      } finally {
        setLoading(false);
      }
    };

    loadResources();
  }, [catalogApi, environmentTypeFilter]);

  // 获取环境类型对应的颜色
  const getEnvColor = (
    envType: string,
  ): 'default' | 'primary' | 'secondary' => {
    switch (envType) {
      case 'test':
        return 'default';
      case 'staging':
        return 'primary';
      case 'production':
        return 'secondary';
      default:
        return 'default';
    }
  };

  const fieldId = idSchema?.$id || 'knative-resource-picker';

  return (
    <FormControl
      margin="normal"
      required={required}
      error={(rawErrors?.length ?? 0) > 0 && !formData}
      fullWidth
    >
      <InputLabel htmlFor={fieldId}>
        {schema?.title || 'Knative Resource'}
      </InputLabel>
      <Select
        id={fieldId}
        value={formData || ''}
        onChange={e => onChange(e.target.value as string)}
        disabled={loading}
      >
        {loading && (
          <MenuItem value="" disabled>
            加载中...
          </MenuItem>
        )}
        {error && (
          <MenuItem value="" disabled>
            加载失败: {error}
          </MenuItem>
        )}
        {!loading && !error && resources.length === 0 && (
          <MenuItem value="" disabled>
            无可用 Knative Resource（请先创建）
          </MenuItem>
        )}
        {resources.map(resource => (
          <MenuItem key={resource.name} value={resource.name}>
            <Box display="flex" alignItems="center" width="100%">
              <Chip
                label={resource.environmentType.toUpperCase()}
                size="small"
                color={getEnvColor(resource.environmentType)}
                style={{ marginRight: 12, minWidth: 80 }}
              />
              <Box>
                <Typography variant="body2" style={{ fontWeight: 500 }}>
                  {resource.title}
                </Typography>
                <Typography variant="caption" color="textSecondary">
                  {resource.namespace} @ {resource.clusterName}
                </Typography>
              </Box>
            </Box>
          </MenuItem>
        ))}
      </Select>
      {(rawErrors?.length ?? 0) > 0 && !formData && (
        <FormHelperText error>此字段为必填项</FormHelperText>
      )}
      {formData &&
        (() => {
          const selected = resources.find(r => r.name === formData);
          return selected ? (
            <FormHelperText>
              已选择: {selected.clusterName} 集群、{selected.namespace} 命名空间
            </FormHelperText>
          ) : null;
        })()}
    </FormControl>
  );
};

